import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { 
  deleteCaseById,
  fetchCpdAnalyticsSummary,
  fetchCpdAnalyticsByDimension,
  fetchCpdUserProgress,
  fetchCpdTopPerformers,
} from "../services";
import { useMemo } from "react";
import _ from "lodash";

export const useDeleteCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCaseById,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });
};

/**
 * Hook to fetch CPD analytics summary
 * @param {Object} filters - Filter parameters for the query
 * @returns {Object} Query result with summary data
 */
export const useCpdAnalyticsSummary = (filters = {}) => {
  const params = useMemo(() => {
    const { dateRange, gender, designationUid, hospitalUid, districtUid, minCpdPoints, maxCpdPoints } = filters;
    
    const queryParams = {};
    
    if (dateRange?.length === 2) {
      queryParams.startDate = dateRange[0].toISOString();
      queryParams.endDate = dateRange[1].toISOString();
    }
    
    if (gender) queryParams.gender = gender;
    if (designationUid) queryParams.designationUid = designationUid;
    if (hospitalUid) queryParams.hospitalUid = hospitalUid;
    if (districtUid) queryParams.districtUid = districtUid;
    if (minCpdPoints !== undefined) queryParams.minCpdPoints = minCpdPoints;
    if (maxCpdPoints !== undefined) queryParams.maxCpdPoints = maxCpdPoints;
    
    return queryParams;
  }, [filters]);

  return useQuery({
    queryKey: ["cpdAnalyticsSummary", params],
    queryFn: () => fetchCpdAnalyticsSummary(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch CPD analytics grouped by a specific dimension
 * @param {string} dimension - Dimension to group by (gender, designation, hospital, district)
 * @param {Object} filters - Filter parameters for the query
 * @returns {Object} Query result with dimension data
 */
export const useCpdAnalyticsByDimension = (dimension, filters = {}) => {
  const params = useMemo(() => {
    const { dateRange } = filters;
    
    const queryParams = {};
    
    if (dateRange?.length === 2) {
      queryParams.startDate = dateRange[0].toISOString();
      queryParams.endDate = dateRange[1].toISOString();
    }
    
    return queryParams;
  }, [filters]);

  return useQuery({
    queryKey: ["cpdAnalyticsByDimension", dimension, params],
    queryFn: () => fetchCpdAnalyticsByDimension(dimension, params),
    enabled: !!dimension,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch CPD user progress with pagination
 * @param {Object} params - Query parameters including pagination and filters
 * @returns {Object} Query result with paginated user progress data
 */
export const useCpdUserProgress = (params = {}) => {
  const queryParams = useMemo(() => {
    const { 
      page = 0, 
      size = 20, 
      dateRange,
      gender,
      designationUid,
      hospitalUid,
      districtUid,
      minCpdPoints,
      maxCpdPoints,
      isEmailVerified,
      searchKeyword,
      sortField,
      sortOrder,
    } = params;
    
    const qp = { page, size };
    
    if (dateRange?.length === 2) {
      qp.startDate = dateRange[0].toISOString();
      qp.endDate = dateRange[1].toISOString();
    }
    
    if (gender) qp.gender = gender;
    if (designationUid) qp.designationUid = designationUid;
    if (hospitalUid) qp.hospitalUid = hospitalUid;
    if (districtUid) qp.districtUid = districtUid;
    if (minCpdPoints !== undefined) qp.minCpdPoints = minCpdPoints;
    if (maxCpdPoints !== undefined) qp.maxCpdPoints = maxCpdPoints;
    if (isEmailVerified !== undefined) qp.isEmailVerified = isEmailVerified;
    if (searchKeyword) qp.searchKeyword = searchKeyword;
    if (sortField) qp.sortField = sortField;
    if (sortOrder) qp.sortOrder = sortOrder;
    
    return qp;
  }, [params]);

  return useQuery({
    queryKey: ["cpdUserProgress", queryParams],
    queryFn: () => fetchCpdUserProgress(queryParams),
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch top CPD performers
 * @param {number} limit - Number of top performers to fetch
 * @returns {Object} Query result with top performers data
 */
export const useCpdTopPerformers = (filters = {}) => {
  const params = useMemo(() => {
    const { dateRange, limit = 10 } = filters;
    
    const queryParams = { limit };
    
    if (dateRange?.length === 2) {
      queryParams.startDate = dateRange[0].toISOString();
      queryParams.endDate = dateRange[1].toISOString();
    }
    
    return queryParams;
  }, [filters]);

  return useQuery({
    queryKey: ["cpdTopPerformers", params],
    queryFn: () => fetchCpdTopPerformers(params),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to compute comparison metrics between current and previous period
 * @param {Object} currentSummary - Current period summary data
 * @param {Object} previousSummary - Previous period summary data
 * @returns {Object} Computed comparison metrics
 */
export const useCpdComparisonMetrics = (currentSummary, previousSummary) => {
  return useMemo(() => {
    if (!currentSummary || !previousSummary) {
      return null;
    }

    const calculateChange = (current, previous) => {
      if (!previous) return { value: current, change: 0, changePercent: 0 };
      const change = current - previous;
      const changePercent = previous > 0 ? (change / previous) * 100 : 0;
      return { value: current, change, changePercent };
    };

    return {
      totalUsers: calculateChange(currentSummary.totalUsers, previousSummary.totalUsers),
      activeUsers: calculateChange(currentSummary.totalActiveUsers, previousSummary.totalActiveUsers),
      completedQuizzes: calculateChange(currentSummary.totalCompletedQuizzes, previousSummary.totalCompletedQuizzes),
      completedCases: calculateChange(currentSummary.totalCompletedCases, previousSummary.totalCompletedCases),
      completedGuidelines: calculateChange(currentSummary.totalCompletedGuidelines, previousSummary.totalCompletedGuidelines),
      averageCompletionRate: calculateChange(currentSummary.averageCompletionRate, previousSummary.averageCompletionRate),
    };
  }, [currentSummary, previousSummary]);
};

/**
 * Hook to process dimension data for chart visualization
 * @param {Array} dimensionData - Raw dimension data from API
 * @param {string} dimensionType - Type of dimension (gender, designation, etc.)
 * @returns {Object} Processed data for charts
 */
export const useCpdDimensionChartData = (dimensionData, dimensionType) => {
  return useMemo(() => {
    if (!dimensionData?.length) {
      return { chartData: [], totalUsers: 0, totalPoints: 0 };
    }

    const chartData = dimensionData.map(item => ({
      name: item.dimensionValue || "(Unknown)",
      userCount: item.userCount || 0,
      completedQuizzes: item.completedQuizzes || 0,
      completedCases: item.completedCases || 0,
      completedGuidelines: item.completedGuidelines || 0,
      averageProgress: item.averageProgress || 0,
      totalCpdPoints: item.totalCpdPoints || 0,
    }));

    const totalUsers = _.sumBy(chartData, 'userCount');
    const totalPoints = _.sumBy(chartData, 'totalCpdPoints');

    return { chartData, totalUsers, totalPoints };
  }, [dimensionData]);
};
